import { useEffect } from "react";

import Link from "next/link";

import CircularLoader from "shared/components/atoms/CircularLoader";
import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import InfinityScroll from "shared/components/atoms/InfinityScroll";
import { useUserNotificationsPaginationService } from "shared/components/organisms/AppNotification/hooks/useUserNotificationsPaginationService";
import { useUserNotificationsService } from "shared/components/organisms/AppNotification/hooks/useUserNotificationsService";
import { getNotificationComponent } from "shared/components/organisms/AppNotification/utils/getNotificationComponent";
import { useTopbarContext } from "shared/providers/Layout/hooks/useTopbarContext";
import { useAuthContext } from "shared/utils/hooks/useAuthContext";
import openNotificationWithIcon from "shared/utils/openNotificationWithIcon";

import { ConsolePageForm } from "@Console/components/molecules/ConsolePageForm";
import { XIcon } from "@Console/integrations/notazz/assets/icons/XIcon";

import { AppNotificationContainer } from "./styles";

const AppNotification = () => {
  const { loggedUser } = useAuthContext();
  const {
    closeNotifications,
    notificationsIsOpen,
    notifications,
    setHasNewNotifications,
  } = useTopbarContext();
  const { loadPaginatedNotifications, paginationInfo, isNotificationsLoading } =
    useUserNotificationsPaginationService();
  const { verifyIfHasNewNotifications } = useUserNotificationsService();

  useEffect(() => {
    const initialFetch = async () => {
      const response = await loadPaginatedNotifications();

      if (!response)
        return openNotificationWithIcon(
          "error",
          "Não foi possível carregar as notificações no momento. Por favor, tente novamente mais tarde.",
        );
    };

    if (notificationsIsOpen) initialFetch();
  }, [notificationsIsOpen]);

  useEffect(() => {
    const initialFetch = async () => {
      const result = await verifyIfHasNewNotifications();

      setHasNewNotifications(result);
    };

    if (!!loggedUser) initialFetch();
  }, [loggedUser]);

  const shouldShowLoadedAllNotificationsMessage =
    paginationInfo?.isLastPage && !isNotificationsLoading;
  const shouldRenderInfinityScroll =
    notificationsIsOpen &&
    !paginationInfo?.isLastPage &&
    !isNotificationsLoading;

  return (
    <AppNotificationContainer>
      <div className="header">
        Notificações
        <ConsolePageForm.Button
          id="notifications-button"
          variant="transparent"
          onClick={closeNotifications}
        >
          <XIcon />
        </ConsolePageForm.Button>
      </div>

      <ul>
        {notifications?.map((notification) => (
          <ComponentErrorBoundary key={notification?.id}>
            <li>
              {!!notification?.metadata?.link ? (
                <Link href={notification?.metadata?.link} prefetch={false}>
                  {getNotificationComponent(notification)}
                </Link>
              ) : (
                getNotificationComponent(notification)
              )}
            </li>
          </ComponentErrorBoundary>
        ))}

        {isNotificationsLoading && <CircularLoader className="loader" />}

        {shouldShowLoadedAllNotificationsMessage && (
          <li className="empty-message">Não há novas notificações</li>
        )}

        <li>
          {shouldRenderInfinityScroll && (
            <InfinityScroll
              fetchMore={loadPaginatedNotifications}
              useIntersectionRatioProp
            />
          )}
        </li>
      </ul>
    </AppNotificationContainer>
  );
};

export default AppNotification;
